import { ModeEditOutlineOutlined } from '@mui/icons-material';
import { Box, Menu, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { InviteMembers } from 'src/pages/MemberManagement/components';

import { Button, ModalContentContainer, TextField } from '@/components';
import { useForm } from '@/hooks';
import { useCreateNewTeamMutation } from '@/store';
import { Palette } from '@/theme';

enum Step {
  CREATE_TEAM = 1,
  ADD_MEMBERS = 2,
}

const isSafeCompanyName = (companyName: string) =>
  !/(?:\b[a-z][a-z\d+.-]*:\/\/|\bwww\.|\b[a-z\d-]+(?:\.[a-z\d-]+)+\/(?:\S*)?)/i.test(
    companyName,
  ) && !/<\/?[a-z][^>]*>/i.test(companyName);

export const NewTeamModalContent = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);
  const [step, setStep] = useState<Step>(Step.CREATE_TEAM);
  const { form, setForm, errors, setErrors } = useForm<{
    companyName: string;
    color: `content.${keyof Palette['content']}`;
  }>({
    companyName: '',
    color: 'content.blue3',
  });
  const [createNewTeam] = useCreateNewTeamMutation();
  const theme = useTheme();

  return (
    <ModalContentContainer stretch>
      {step === Step.CREATE_TEAM ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
            width: 700,
          }}>
          <Typography variant="h1">Create Your Team</Typography>
          <Box
            sx={{
              mt: 12.5,
              width: 130,
              height: 130,
              borderRadius: 6,
              backgroundColor: form.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}>
            <Typography
              sx={{
                color: 'white',
                fontSize: 73,
              }}>
              {form.companyName ? form.companyName[0].toUpperCase() : 'T'}
            </Typography>
            <Box
              sx={{
                position: 'absolute',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'grey50',
                top: 100,
                left: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
              }}
              onClick={e => {
                setAnchorEl(e.currentTarget);
              }}>
              <ModeEditOutlineOutlined
                sx={{
                  width: 24,
                  height: 24,
                }}
              />
            </Box>
          </Box>
          <Box sx={{ mt: 5, width: 300 }}>
            <TextField
              label="Team Name"
              placeholder="Enter your team name"
              fullWidth
              value={form.companyName}
              error={
                !!errors.companyName || !isSafeCompanyName(form.companyName)
              }
              helperText={errors.companyName}
              onChange={e => {
                setForm({ ...form, companyName: e.target.value });
                setErrors({ ...errors, companyName: '' });
              }}
            />
          </Box>
          <Box sx={{ mt: 10, width: 200, alignSelf: 'flex-end' }}>
            <Button
              variant="primary"
              label="Save and Continue"
              skinny={false}
              disabled={
                !!errors.companyName ||
                !isSafeCompanyName(form.companyName) ||
                !form.companyName
              }
              onClick={async () => {
                try {
                  await createNewTeam(form).unwrap();
                  setStep(Step.ADD_MEMBERS);
                } catch (error) {
                  if (
                    (error as { data?: { code: string } })?.data?.code ===
                    'lumiq.account.create_organization.in_another_org'
                  ) {
                    setErrors({
                      ...errors,
                      companyName: 'You are already part of a team',
                    });
                    return;
                  }
                  setErrors({
                    ...errors,
                    companyName: 'Failed to create team',
                  });
                }
              }}
            />
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(undefined)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            slotProps={{
              paper: {
                sx: {
                  overflow: 'visible',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 1,
                    right: 74,
                    width: 10,
                    height: 10,
                    backgroundColor: 'inherit',
                    transform: 'translateY(-50%) rotate(45deg)',
                  },
                },
              },
            }}
            sx={{
              mt: 0.5,
              boxShadow: '0 6px 20px 0 rgba(0, 0, 0, 0.40);',
            }}>
            <Box
              sx={{
                p: 1.5,
                width: 158,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.25,
              }}>
              {Object.entries(theme.palette.content as Palette['content']).map(
                ([name, color]) => (
                  <Box
                    key={color}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: color,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setForm({
                        ...form,
                        color: `content.${name as keyof Palette['content']}`,
                      });
                      setAnchorEl(undefined);
                    }}
                  />
                ),
              )}
            </Box>
          </Menu>
        </Box>
      ) : (
        <InviteMembers activeMemberEmails={[]} onSuccess={() => {}} />
      )}
    </ModalContentContainer>
  );
};
